import React from "react";
import { Link } from "react-router-dom";

import * as Yup from "yup";
import TextInput from "../components/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { handleError } from "../utils/functions";
import SubmitButton from "../components/SubmitButton";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios.config";
import Textarea from "./Textarea";
import { useTranslation } from "react-i18next";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  subject: Yup.string().required("Subject is Required"),
  message: Yup.string().required("Message is Required"),
});

const Contact = () => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
    reset,
  } = useForm({ resolver: yupResolver(schema), mode: "all" });

  const onSubmit = async (data) => {
    try {
      const { data: res } = await axiosInstance.post("/contact", data);
      if (!res.error) {
        toast.success(res.message);
        reset();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };
  return (
    <section className="contact py-80">
      <div className="container">
        <div className="row gy-5">
          <div className="col-lg-8">
            <div className="contact-box bg-white border border-gray-100 rounded-16 px-24 py-40">
              <form onSubmit={handleSubmit(onSubmit)}>
                <h6 className="mb-32">{t("makeCustomRequest")}</h6>
                <div className="row gy-4">
                  <div className="col-sm-6 col-xs-6">
                    <TextInput
                      name={"name"}
                      placeholder={t("enterFullName")}
                      isRequired
                      label={t("fullName")}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("name", e.target.value)}
                      error={errors.name}
                    />
                  </div>
                  <div className="col-sm-6 col-xs-6">
                    <TextInput
                      name={"email"}
                      placeholder={t("enterEmail")}
                      isRequired
                      label={t("emailAddress")}
                      type={"email"}
                      register={register}
                      onChange={(e) => setValue("email", e.target.value)}
                      error={errors.email}
                    />
                  </div>
                  <div className="col-sm-6 col-xs-6">
                    <TextInput
                      name={"phoneNumber"}
                      placeholder={t("enterPhone")}
                      isRequired
                      label={t("phoneNumber")}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("phoneNumber", e.target.value)}
                      error={errors.phoneNumber}
                    />
                  </div>
                  <div className="col-sm-6 col-xs-6">
                    <TextInput
                      name={"subject"}
                      placeholder={t("enterSubject")}
                      isRequired
                      label={t("subject")}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("subject", e.target.value)}
                      error={errors.subject}
                    />
                  </div>
                  <div className="col-sm-12">
                    <Textarea
                      name={"message"}
                      placeholder={t("enterMessage")}
                      isRequired
                      label={t("message")}
                      register={register}
                      error={errors.message}
                    />
                  </div>
                  <div className="col-sm-12 mt-32">
                    <SubmitButton className="py-18 px-32 rounded-8" isSubmitting={isSubmitting}>
                      {t("getQuote")}
                    </SubmitButton>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="contact-box bg-white border border-gray-100 rounded-16 px-24 py-40">
              <h6 className="mb-48">{t("getInTouch")}</h6>
              <div className="flex-align gap-16 mb-16">
                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0">
                  <i className="ph-fill ph-phone-call" />
                </span>
                <Link to="/tel:+00123456789" className="text-md text-gray-900 hover-text-main-600">
                  +00 123 456 789
                </Link>
              </div>
              <div className="flex-align gap-16 mb-16">
                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0">
                  <i className="ph-fill ph-envelope" />
                </span>
                <Link
                  to="/mailto:support24@marketpro.com"
                  className="text-md text-gray-900 hover-text-main-600"
                >
                  support24@marketpro.com
                </Link>
              </div>
              <div className="flex-align gap-16 mb-0">
                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0">
                  <i className="ph-fill ph-map-pin" />
                </span>
                <span className="text-md text-gray-900 ">{t("address")}</span>
              </div>
            </div>
            <div className="mt-24 flex-align flex-wrap gap-16">
              <Link
                to="#"
                className="bg-neutral-600 hover-bg-main-600 rounded-8 p-10 px-16 flex-between flex-wrap gap-8 flex-grow-1"
              >
                <span className="text-white fw-medium">{t("getSupportOnCall")}</span>
                <span className="w-36 h-36 bg-main-600 rounded-8 flex-center text-xl text-white">
                  <i className="ph ph-headset" />
                </span>
              </Link>
              <Link
                to="#"
                className="bg-neutral-600 hover-bg-main-600 rounded-8 p-10 px-16 flex-between flex-wrap gap-8 flex-grow-1"
              >
                <span className="text-white fw-medium">{t("getDirection")}</span>
                <span className="w-36 h-36 bg-main-600 rounded-8 flex-center text-xl text-white">
                  <i className="ph ph-map-pin" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
